import { CheckIcon } from '@radix-ui/react-icons';
import { Portal } from '@radix-ui/react-portal';
import clsx from 'clsx';
import _range from 'lodash/range';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import ChevronDown from '@/assets/icons/chevron-down.svg';
import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Typography } from '@/components/ui/typography';

import { Skeleton } from './skeleton';

type Option = {
  label: string;
  value: string | boolean | number | undefined;
};

type SelectProps<T> = {
  id: string;
  options: Option[];
  selectedValues: T[];
  onApply: (selected: T[], key: string) => void;
  isMultiSelect?: boolean;
  prefixText?: string;
  placeholder?: string;
  isLoading?: boolean;
  query?: string;
  setQuery?: (q?: string) => void;
  className?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
};

function Select<T>({
  id,
  options = [],
  selectedValues,
  onApply,
  isMultiSelect = true,
  prefixText,
  placeholder,
  isLoading,
  query = '',
  setQuery,
  className,
  isClearable = false,
  isDisabled = false,
}: SelectProps<T>) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [tempSelectedValues, setTempSelectedValues] = useState(selectedValues);

  useEffect(() => {
    setTempSelectedValues(selectedValues);
  }, [selectedValues]);

  const toggleSelection = useCallback(
    (value: T) => {
      if (isMultiSelect) {
        setTempSelectedValues(prev =>
          prev.includes(value)
            ? prev.filter(item => item !== value)
            : [...prev, value],
        );
      } else {
        setTempSelectedValues([value]);
        onApply([value], id);
        setOpen(false);
      }
    },
    [isMultiSelect, id, onApply, setOpen],
  );

  const mainText = useMemo(() => {
    if (isMultiSelect) {
      return tempSelectedValues.length > 0
        ? `${tempSelectedValues.length} selected`
        : 'All';
    }

    const selectedOption = options.find(
      opt => opt.value === tempSelectedValues[0],
    );
    return selectedOption?.label ?? null;
  }, [tempSelectedValues, isMultiSelect, options]);

  const handleApply = () => {
    onApply(tempSelectedValues, id);
    setOpen(false);
  };

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setTempSelectedValues([]);
    onApply([], id);
    setOpen(false);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) setTempSelectedValues(selectedValues);
    setQuery?.();
    setOpen(open);
  };

  return (
    <div className="flex flex-col space-y-2">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="secondary"
            className={className}
            disabled={isDisabled}
          >
            <ButtonText
              prefixText={prefixText}
              mainText={mainText}
              placeholder={placeholder}
              isLoading={isLoading}
            />
            {isClearable && tempSelectedValues.length > 0 ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                className="ml-auto h-5 w-5 cursor-pointer"
              >
                <CloseIcon className="text-fx-neutral-400 hover:text-fx-primary" />
              </span>
            ) : (
              <img src={ChevronDown} alt="Chevron down" className="ml-auto" />
            )}
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent
            className="pointer-events-auto min-w-[200px] !p-0"
            align="start"
          >
            <Command shouldFilter={false}>
              {setQuery && (
                <CommandInput
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Search...',
                  })}
                  className="h-9"
                  value={query}
                  onValueChange={setQuery}
                />
              )}
              <CommandList>
                <Content isLoading={isLoading} options={options}>
                  <CommandGroup>
                    {options.map(option => {
                      const isSelected = tempSelectedValues.some(
                        value => value === option.value,
                      );
                      return (
                        <CommandItem
                          key={option.label}
                          onSelect={() => toggleSelection(option.value as T)}
                        >
                          {option.label}
                          <CheckIcon
                            className={clsx(
                              'ml-auto h-4 w-4',
                              isSelected ? 'opacity-100' : 'opacity-0',
                            )}
                          />
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </Content>
              </CommandList>
            </Command>
            {isMultiSelect && (
              <div className="flex justify-between border-t border-gray-200 p-1.5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClear}
                  className="text-fx-primary"
                >
                  <FormattedMessage defaultMessage="Clear" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleApply}
                  className="text-fx-primary"
                >
                  <FormattedMessage defaultMessage="Apply" />
                </Button>
              </div>
            )}
          </PopoverContent>
        </Portal>
      </Popover>
    </div>
  );
}

interface ButtonTextProps {
  prefixText?: string;
  mainText: string | null;
  placeholder?: string;
  isLoading?: boolean;
}

function ButtonText({
  prefixText,
  mainText,
  placeholder,
  isLoading,
}: ButtonTextProps) {
  if (isLoading) return <Skeleton className="h-4 w-2/3" />;

  if (prefixText || mainText)
    return (
      <>
        {prefixText && <Typography variant="body1">{prefixText}</Typography>}
        {mainText && (
          <Typography variant="body2" className="-ml-0.5">
            {mainText}
          </Typography>
        )}
      </>
    );

  if (placeholder)
    return <Typography variant="inactive1">{placeholder}</Typography>;

  return null;
}

interface ContentProps {
  isLoading?: boolean;
  options: Option[];
  children: React.ReactNode;
}

function Content({ isLoading, options, children }: ContentProps) {
  if (isLoading)
    return (
      <div className="space-y-4 p-4">
        {_range(5).map(i => (
          <Skeleton key={i} className="h-5 w-full" />
        ))}
      </div>
    );

  if (!isLoading && options.length === 0)
    return (
      <CommandEmpty>
        <Typography variant="body1">
          <FormattedMessage defaultMessage="No options found!" />
        </Typography>
      </CommandEmpty>
    );

  return <>{children}</>;
}

type FormSelectProps<T, TFieldValues extends FieldValues = FieldValues> = {
  name: FieldPath<TFieldValues>;
  control: Control<TFieldValues>;
} & Omit<SelectProps<T>, 'id' | 'selectedValues' | 'onApply'>;

/**
 * A wrapper around the Select component that integrates with react-hook-form.
 */
function FormSelect<T, TFieldValues extends FieldValues>({
  name,
  control,
  ...props
}: FormSelectProps<T, TFieldValues>) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select
          id={name}
          selectedValues={field.value ? [field.value] : []}
          onApply={selected => {
            field.onChange(selected[0] ?? null);
          }}
          {...props}
        />
      )}
    />
  );
}

export { type Option, type SelectProps, FormSelect, Select };
